import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/atoms'
import { RepeatableFormSection } from '@/components/molecules/repeatable-form-section'
import { isValidEmail } from '@/shared/helpers/validation'

export interface PersonDetails {
  id?: string
  name: string
  address: string
  email: string
}

interface PersonFormSectionProps {
  entries: PersonDetails[]
  title: (index: number) => string
  namePlaceholderKey: string
  onAdd: () => void
  onRemove: (index: number) => void
  onUpdate: (index: number, field: keyof PersonDetails, value: string) => void
  addLabel: string
  showAdd?: boolean
  showErrors?: boolean
  variant?: 'default' | 'spouse'
  icon?: React.ReactNode
}

export function PersonFormSection({
  entries,
  title,
  namePlaceholderKey,
  onAdd,
  onRemove,
  onUpdate,
  addLabel,
  showAdd,
  showErrors,
  variant = 'default',
  icon,
}: PersonFormSectionProps) {
  const { t } = useTranslation()
  return (
    <RepeatableFormSection
      entries={entries}
      title={title}
      onAdd={onAdd}
      onRemove={onRemove}
      addLabel={addLabel}
      showAdd={showAdd}
      headerIcon={icon}
      fieldsContainerClassName={
        variant === 'spouse'
          ? 'flex flex-col gap-6'
          : 'flex flex-col gap-6 md:flex-row md:flex-wrap lg:grid lg:grid-cols-2'
      }
      renderFields={(entry, i) => {
        const isReadOnly = !!entry.id
        const hasAnyField =
          variant !== 'spouse' && !!(entry.name || entry.email)
        return (
          <>
            <div className="w-full flex-1">
              <Input
                label={t('onboarding.renters.form.whoIsCovered.fullName')}
                placeholder={t(
                  `onboarding.renters.form.whoIsCovered.${namePlaceholderKey}`,
                )}
                value={entry.name}
                onChange={(e) => onUpdate(i, 'name', e.target.value)}
                className="gap-3"
                disabled={isReadOnly}
                error={
                  showErrors && hasAnyField && !entry.name
                    ? t('common.required')
                    : undefined
                }
              />
            </div>

            {variant !== 'spouse' && (
              <div className="w-full flex-1">
                <Input
                  label={t('onboarding.renters.form.whoIsCovered.email')}
                  placeholder={t(
                    'onboarding.renters.form.whoIsCovered.emailPlaceholder',
                  )}
                  type="email"
                  value={entry.email}
                  onChange={(e) => onUpdate(i, 'email', e.target.value)}
                  className="gap-3"
                  disabled={isReadOnly}
                  error={
                    showErrors && hasAnyField
                      ? !entry.email
                        ? t('common.required')
                        : !isValidEmail(entry.email)
                          ? t('common.invalidEmail')
                          : undefined
                      : undefined
                  }
                />
              </div>
            )}
          </>
        )
      }}
    />
  )
}
